import React, { useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import { Icon } from '@iconify/react'
import { colors } from 'src/components/app-ui'

// Mini Course Generator's Zapier partner app; the public Partner API answers
// browser requests for it (zap templates, app search).
const ZAPIER_CLIENT_ID = 'BBNMgMIiTPRXdNLYTAyQQz5rDmoYPOy6YomMQvdc'
const ZAPIER_API = 'https://api.zapier.com/v1'
const MCG_ICON =
  'https://cdn.zapier.com/storage/services/7ac81e6a5145bd2a80b7d78dffb869da.png'
// TODO(zapier): there's no documented way to hand the prompt to Zapier's AI
// builder, so Generate opens the Zap editor and copies the prompt.
const ZAP_EDITOR_URL = 'https://zapier.com/app/editor'
const WORKFLOW_COUNT = 4

const field = {
  border: '1px solid #D0D5D9',
  borderRadius: '6px',
  padding: '10px 12px',
  background: '#FFFFFF',
  fontFamily: 'inherit',
  fontSize: '14px',
  color: colors.navy,
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  prompt: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '16px',
    border: '1px solid #FFE1D1',
    borderRadius: '10px',
    background: '#FFF8F4',
  },
  promptTitle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '15px',
    fontWeight: 700,
    color: colors.navy,
  },
  beta: {
    padding: '1px 6px',
    border: '1px solid #D0D5D9',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    color: colors.muted,
  },
  promptRow: {
    display: 'flex',
    gap: '8px',
  },
  promptInput: {
    ...field,
    flex: 1,
    minWidth: 0,
    outline: 'none',
  },
  generate: {
    padding: '0 18px',
    border: 0,
    borderRadius: '6px',
    background: '#201515',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: 600,
    color: '#FFFFFF',
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  example: {
    fontSize: '12px',
    color: colors.muted,
  },
  connect: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)',
    gap: '12px',
    alignItems: 'end',
  },
  connectField: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: 0,
  },
  connectLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: colors.navy,
  },
  appBox: {
    ...field,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  appIcon: {
    width: 18,
    height: 18,
    flex: 'none',
    borderRadius: '4px',
  },
  plus: {
    width: 32,
    height: 32,
    marginBottom: 4,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    background: colors.hover,
    color: colors.muted,
  },
  search: {
    ...field,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: colors.subtle,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: 0,
    outline: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontSize: '14px',
    color: colors.navy,
  },
  results: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 5,
    display: 'flex',
    flexDirection: 'column',
    padding: '4px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    background: '#FFFFFF',
    boxShadow:
      '0 12px 24px -4px rgba(145,158,171,.24), 0 0 2px rgba(145,158,171,.3)',
  },
  result: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 10px',
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '14px',
    textAlign: 'left',
    color: colors.navy,
    '&:hover': {
      background: colors.hover,
    },
  },
  workflows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  workflowsTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.navy,
  },
  workflow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '14px',
    padding: '12px 16px',
    border: `1px solid ${colors.border}`,
    borderRadius: '10px',
  },
  workflowText: {
    flex: 1,
    minWidth: 200,
  },
  workflowName: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.navy,
  },
  workflowApps: {
    marginTop: 2,
    fontSize: '12px',
    color: colors.muted,
  },
  useWorkflow: {
    padding: '7px 14px',
    border: 0,
    borderRadius: '6px',
    background: '#FF4F00',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 600,
    color: '#FFFFFF',
    whiteSpace: 'nowrap',
  },
  powered: {
    fontSize: '11px',
    color: colors.subtle,
  },
})

const zapierGet = async (path, params) => {
  const query = new URLSearchParams({ client_id: ZAPIER_CLIENT_ID, ...params })
  const response = await fetch(`${ZAPIER_API}/${path}?${query}`)
  if (!response.ok) throw new Error(`Zapier ${path}: ${response.status}`)
  return response.json()
}

const openInNewTab = url => window.open(url, '_blank', 'noopener,noreferrer')

/**
 * Integrations › Automations: build a Zap without leaving the page. Popular
 * workflows and the app search come from Zapier's Partner API; each opens on
 * zapier.com.
 */
const ZapierBuilder = () => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const searchRef = useRef(null)
  const [prompt, setPrompt] = useState('')
  const [workflows, setWorkflows] = useState([])
  const [query, setQuery] = useState('')
  const [apps, setApps] = useState([])

  useEffect(() => {
    let cancelled = false
    zapierGet('zap-templates', { limit: WORKFLOW_COUNT })
      .then(templates => !cancelled && setWorkflows(templates || []))
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const term = query.trim()
    if (!term) {
      setApps([])
      return undefined
    }
    let cancelled = false
    const timer = setTimeout(() => {
      zapierGet('apps', { title_search: term, limit: 6 })
        .then(result => !cancelled && setApps(result?.objects || []))
        .catch(() => !cancelled && setApps([]))
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query])

  useEffect(() => {
    if (!apps.length) return undefined
    const onDown = event => {
      if (searchRef.current && !searchRef.current.contains(event.target))
        setApps([])
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [apps.length])

  const generate = async () => {
    const text = prompt.trim()
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      enqueueSnackbar('Prompt copied. Paste it into Zapier to build the Zap.', {
        variant: 'info',
      })
    } catch (_e) {
      // Without clipboard access the editor still opens.
    }
    openInNewTab(ZAP_EDITOR_URL)
  }

  const openApp = app => {
    setApps([])
    setQuery('')
    openInNewTab(app.url)
  }

  return (
    <div className={classes.root}>
      <div className={classes.prompt}>
        <div className={classes.promptTitle}>
          What would you like to automate?
          <span className={classes.beta}>AI beta</span>
        </div>
        <div className={classes.promptRow}>
          <input
            className={classes.promptInput}
            value={prompt}
            placeholder="The more details, the better."
            onChange={event => setPrompt(event.target.value)}
            onKeyDown={event => event.key === 'Enter' && generate()}
          />
          <button
            type="button"
            className={classes.generate}
            disabled={!prompt.trim()}
            onClick={generate}
          >
            Generate
          </button>
        </div>
        <div className={classes.example}>
          Example: When a learner completes a course, add them to a Mailchimp
          audience.
        </div>
      </div>

      <div className={classes.connect}>
        <div className={classes.connectField}>
          <span className={classes.connectLabel}>Connect this app…</span>
          <div className={classes.appBox}>
            <img src={MCG_ICON} alt="" className={classes.appIcon} />
            Mini Course Generator
          </div>
        </div>
        <span className={classes.plus}>
          <Icon icon="tabler:plus" width={16} height={16} />
        </span>
        <div className={classes.connectField} ref={searchRef}>
          <span className={classes.connectLabel}>with this one!</span>
          <div className={classes.search}>
            <Icon icon="tabler:search" width={16} height={16} />
            <input
              className={classes.searchInput}
              value={query}
              placeholder="Search for thousands of apps"
              onChange={event => setQuery(event.target.value)}
              onKeyDown={event =>
                event.key === 'Enter' && apps[0] && openApp(apps[0])
              }
            />
          </div>
          {apps.length > 0 && (
            <div className={classes.results}>
              {apps.map(app => (
                <button
                  key={app.uuid}
                  type="button"
                  className={classes.result}
                  onClick={() => openApp(app)}
                >
                  <img src={app.image} alt="" className={classes.appIcon} />
                  {app.title}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {workflows.length > 0 && (
        <div className={classes.workflows}>
          <div className={classes.workflowsTitle}>Popular workflows</div>
          {workflows.map(workflow => (
            <div key={workflow.id} className={classes.workflow}>
              <div className={classes.workflowText}>
                <div className={classes.workflowName}>{workflow.title}</div>
                <div className={classes.workflowApps}>
                  {(workflow.steps || []).map(step => step.title).join(' + ')}
                </div>
              </div>
              <button
                type="button"
                className={classes.useWorkflow}
                onClick={() => openInNewTab(workflow.create_url)}
              >
                Use this workflow
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={classes.powered}>Powered by Zapier</div>
    </div>
  )
}

export default ZapierBuilder
